import { Injectable, inject } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { AnalyticsLogTypes, AnalyticsPages } from './analytics.constants';
import type { LogParam } from './analytics.types';
import { sanitizePayload } from '../telemetry/telemetry.types';
import { LoggerService } from '../logger/logger.service';
import { AppConfigStore } from '../app-config/app-config.store';
import { environment } from '../../../environments/environment';

const TAG = 'VdiClickEventsService';

/**
 * Interface representing a VDI click event message contract received via postMessage.
 */
export interface VdiClickEventMessage {
  source?: string;
  eventType: string;
  payload?: Record<string, unknown> | string;
  stream: 'click_event';
  sessionId?: string;
  vdiSessionId?: string;
  [key: string]: unknown;
}

/**
 * Service responsible for processing generic VDI click events received from the HyLite iframe.
 *
 * It bridges VDI click events to the PWA's standard AnalyticsService (`POST api/log/user`)
 * without polluting or engaging the VDI Telemetry IndexedDB/batch sync pipeline.
 */
@Injectable({ providedIn: 'root' })
export class VdiClickEventsService {
  private analyticsService = inject(AnalyticsService);
  private appConfigStore = inject(AppConfigStore);
  private logger = inject(LoggerService);

  /**
   * Checks whether VDI click event logging is enabled via environment and dynamic CMS config.
   */
  isEnabled(): boolean {
    const envEnabled = (environment as unknown as { vdiClickEvents?: { enabled?: boolean } }).vdiClickEvents?.enabled ?? true;
    if (!envEnabled) return false;

    return this.appConfigStore.isVdiClickEventsEnabled();
  }

  /**
   * Processes an incoming click event message from the VDI iframe and forwards it to `AnalyticsService.log`.
   *
   * @param message - The raw message object received from window postMessage
   * @param activeSessionId - The active PWA VDI session ID for fallback correlation
   */
  processClickEvent(message: Record<string, unknown>, activeSessionId: string | null): void {
    if (!this.isEnabled()) {
      this.logger.debug(TAG, 'click event dropped: VDI click events feature is disabled');
      return;
    }

    const eventType = typeof message['eventType'] === 'string' ? message['eventType'].trim() : '';
    if (!eventType) {
      this.logger.warn(TAG, 'click event rejected: missing or empty eventType', message);
      return;
    }

    try {
      // 1. Resolve and sanitize payload
      let sanitizedPayload: Record<string, unknown> = {};
      const rawPayload = message['payload'];

      if (typeof rawPayload === 'string') {
        try {
          const parsed = JSON.parse(rawPayload);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            sanitizedPayload = sanitizePayload(parsed as Record<string, unknown>);
          } else {
            sanitizedPayload = { message: rawPayload };
          }
        } catch {
          sanitizedPayload = { message: rawPayload };
        }
      } else if (rawPayload && typeof rawPayload === 'object' && !Array.isArray(rawPayload)) {
        sanitizedPayload = sanitizePayload(rawPayload as Record<string, unknown>);
      } else {
        const copy = { ...message };
        delete copy['stream'];
        delete copy['source'];
        delete copy['eventType'];
        delete copy['type'];
        delete copy['sessionId'];
        delete copy['vdiSessionId'];
        sanitizedPayload = sanitizePayload(copy);
      }

      // 2. Extract page_title (use payload.page_title if provided, otherwise default to VDI_SESSION)
      let pageTitle: string = AnalyticsPages.VDI_SESSION;
      if (typeof sanitizedPayload['page_title'] === 'string' && sanitizedPayload['page_title'].trim()) {
        pageTitle = sanitizedPayload['page_title'].trim();
        delete sanitizedPayload['page_title'];
      }

      // 3. Resolve VDI Session ID:
      // - Primary: `payload.vdiSessionId` (standard VDI telemetry format) or top-level `message.vdiSessionId`.
      // - Fallback: `activeSessionId` recorded by PWA when launching the VDI session.
      // - If unavailable, omit `vdi_session_id` entirely.
      const vdiSessionId =
        (typeof sanitizedPayload['vdiSessionId'] === 'string' && sanitizedPayload['vdiSessionId'].trim()) ||
        (typeof message['vdiSessionId'] === 'string' && message['vdiSessionId'].trim()) ||
        (typeof activeSessionId === 'string' && activeSessionId.trim()) ||
        undefined;

      // Standardize: Remove camelCase key so generic spreading (...sanitizedPayload) only sends snake_case `vdi_session_id`
      delete sanitizedPayload['vdiSessionId'];

      // 4. Construct LogParam (standardized with only `vdi_session_id`)
      const logParam: LogParam = {
        logType: AnalyticsLogTypes.CLICK_EVENT,
        ...(vdiSessionId ? { vdi_session_id: vdiSessionId } : {}),
        ...sanitizedPayload,
      };

      // 5. Local/Dev Debug Log
      const buildType = environment.buildType as string;
      if (buildType === 'dev' || buildType === 'local') {
        console.log(
          `%c[VDI Click Event]%c ${eventType}`,
          'background: #059669; color: #FFFFFF; font-weight: bold; padding: 2px 6px; border-radius: 4px 0 0 4px;',
          'background: #10B981; color: #FFFFFF; font-weight: bold; padding: 2px 6px; border-radius: 0 4px 4px 0;',
          {
            eventType,
            page_title: pageTitle,
            logParam,
          },
        );
      }

      // 6. Forward to AnalyticsService
      this.analyticsService.log(eventType, pageTitle, logParam);
      this.logger.debug(TAG, 'click event forwarded to AnalyticsService', { eventType, pageTitle });
    } catch (err) {
      this.logger.error(TAG, 'error while processing VDI click event', err);
    }
  }
}
